import { useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { GTFonts, useTheme, type Theme } from '../../../../theme';
import { usePickerItemColor } from '../../../../hooks/usePickerItemColor';
import { borderWidth, fs, s, v } from '../../../../utils/libs';

/**
 * Shared styling for the extracted product form field components (US3,
 * T053). Values are copied verbatim from NewProduct.tsx so the extraction
 * has zero visual change.
 */

export const createProductFormFieldStyles = (theme: Theme) => {
  // `textDark` is an optional theme extension mirrored from NewProduct.tsx,
  // which historically typed its theme as `any`.
  const textDark = (theme.colors as any).textDark ?? theme.colors.text;
  return StyleSheet.create({
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      marginBottom: v(4),
      color: textDark,
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
        marginTop: v(24),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    accordionHeader: {
      width: '100%',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 0,
      marginHorizontal: 0,
      ...(Platform.OS === 'web' && {
        marginTop: v(24),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    accordionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: textDark,
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
      }),
    },
    accordionChevron: {
      color: theme.colors.text,
      fontSize: fs(20),
      lineHeight: fs(20),
      margin: 0,
      padding: 0,
    },
    input: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      height: v(50),
      fontSize: fs(14),
      lineHeight: fs(20),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        height: v(56),
        fontSize: fs(14),
      }),
    } as any,
    pickerContainer: {
      borderWidth: 1,
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      marginVertical: v(6),
      justifyContent: 'center',
      overflow: 'hidden',
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    picker: {
      fontSize: fs(14),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        height: v(56),
        borderWidth: 0,
        backgroundColor: 'transparent',
        outlineStyle: 'none',
        outlineWidth: 0,
        outlineColor: 'transparent',
        paddingHorizontal: 12,
        fontSize: fs(14),
      }),
    } as any,
    variantSection: {
      gap: v(18),
      marginTop: v(4),
    },
    variantAttributeHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: s(10),
      ...(Platform.OS === 'web' && {
        marginTop: v(24),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    variantAttributeHeaderTitle: {
      ...(Platform.OS === 'web' && {
        marginTop: 0,
        marginBottom: 0,
        paddingBottom: 0,
        borderBottomWidth: 0,
      }),
    },
    attributeAddLink: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
      color: theme.colors.successDark,
      textDecorationLine: 'underline',
    },
    attributeAddChip: {
      paddingHorizontal: s(10),
      paddingVertical: s(6),
      justifyContent: 'center',
    },
    attributeChipContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: s(8),
    },
    attributeChip: {
      paddingHorizontal: s(10),
      paddingVertical: s(6),
      borderRadius: theme.borderRadius.round,
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    attributeChipSelected: {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primary,
    },
    attributeChipText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    attributeChipTextSelected: {
      color: theme.colors.primaryDark,
      fontFamily: GTFonts.semibold,
    },
    variantParentImageLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.text,
    },
    variantParentImageHint: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      marginTop: v(-4),
      marginBottom: v(4),
    },
    priceTypePrompt: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.text,
      marginTop: v(8),
      marginBottom: v(8),
    },
    priceTypeOption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(6),
      marginBottom: v(12),
    },
    priceTypeOptionBody: {
      flex: 1,
    },
    priceTypeOptionLabel: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    priceTypeOptionHint: {
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(15),
      color: theme.colors.textSecondary,
    },
    variantCard: {
      padding: s(14),
      borderRadius: s(12),
      borderWidth: 1,
      borderColor: theme.colors.borderGray,
      backgroundColor: theme.colors.surface,
      gap: v(10),
      marginTop: v(6),
    },
    variantCardDeleted: {
      opacity: 0.6,
    },
    variantCardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    variantCardTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    variantDeleteTouchable: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
    },
    variantDeleteLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(16),
    },
    variantRow: {
      flexDirection: 'row',
      gap: s(8),
    },
    variantInputHalf: {
      flex: 1,
    },
    variantAdditionalAccordionHeader: {
      marginTop: v(6),
    },
    variantAddButton: {
      marginTop: v(4),
      borderRadius: theme.borderRadius.md,
    },
    variantBarcodeList: {
      padding: s(10),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surfaceSecondary,
      gap: v(6),
    },
    variantBarcodeListTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    variantBarcodeListItem: {
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
  });
};

export type ProductFormFieldStyles = ReturnType<
  typeof createProductFormFieldStyles
>;

/**
 * Theme bundle used by every extracted field component: outline props,
 * web-only placeholder softening, picker item colors, and shared styles.
 */
export const useProductFormFieldTheme = () => {
  const { theme } = useTheme();
  const { pickerItemColor, pickerItemColorSecondary } = usePickerItemColor();

  const styles = useMemo(() => createProductFormFieldStyles(theme), [theme]);

  const inputOutlineProps = useMemo(
    () => ({
      outlineColor: theme.colors.borderGray,
      activeOutlineColor: theme.colors.primary,
    }),
    [theme],
  );

  const webPlaceholderTheme = useMemo(() => {
    if (Platform.OS === 'web') {
      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      const placeholderColor = theme.colors.textSecondary
        ? hexToRgba(theme.colors.textSecondary, 0.55)
        : 'rgba(128, 128, 128, 0.55)';

      return {
        theme: {
          fonts: {
            regular: {
              fontFamily: GTFonts.regular,
              fontWeight: '400' as any,
            },
          },
          colors: {
            placeholder: placeholderColor,
            onSurfaceVariant: placeholderColor,
          },
        },
      };
    }
    return {};
  }, [theme.colors.textSecondary]);

  return {
    theme,
    styles,
    inputOutlineProps,
    webPlaceholderTheme,
    pickerItemColor,
    pickerItemColorSecondary,
  };
};
